import { HttpClient, HttpErrorResponse, HttpEventType, HttpResponse } from '@angular/common/http';
import { computed, inject, Injectable, PLATFORM_ID, REQUEST, signal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../environments/environment';
import { TAGConfig } from '../models/tagconfig';
import { TAGUser } from '../models/taguser';
import { APIError } from '../models/apierror';

import { sha256 } from 'js-sha256';
import { TodoPriority, TAGTodo, TodoStatus } from '../models/tagtodo';
import { TAGRequest } from '../models/tagrequest';
import { TAGEvent } from '../models/tagevent';
import { TAGPermission } from '../models/tagpermission';
import { TAGFinanceTransaction } from '../models/tagfinances';
import { TAGInvItemGroup, TAGInvItemType, TAGInvTransaction } from '../models/taginventory';
import { TAGPresence } from '../models/tagpresence';
import { TAGSongRequest } from '../models/tagsongrequest';
import { TAGImageCategory } from '../models/tagimagecategory';
import { TAGImage } from '../models/tagimage';
import { isPlatformServer } from '@angular/common';
import { OAuthInteraction } from './main/oauth/oauth';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private http = inject(HttpClient);
  private request = inject(REQUEST, { optional: true });
  private platformId = inject(PLATFORM_ID);
  private cookieService = inject(CookieService);

  API_URL: string = environment.apiUrl;

  API_SELECTOR_USER: string = 'user/';
  API_SELECTOR_CONFIG: string = 'config/';

  API_SELECTOR_USERS: string = 'users/';
  API_SELECTOR_PRESENCE: string = 'presence/';
  API_SELECTOR_TODOS: string = 'todos/';
  API_SELECTOR_REQUESTS: string = 'requests/';
  API_SELECTOR_EVENTS: string = 'events/';
  API_SELECTOR_SONGREQUESTS: string = 'songrequests/';
  API_SELECTOR_VOTE: string = 'vote/';
  API_SELECTOR_FINANCES: string = 'finances/';

  API_SELECTOR_CATEGORIES: string = 'categories/';
  API_SELECTOR_IMAGES: string = 'images/';
  API_SELECTOR_IMAGE: string = 'image/';
  API_SELECTOR_THUMBNAIL: string = 'thumb/';
  API_SELECTOR_FULLIMAGE: string = 'full/';
  API_SELECTOR_RANDOMIMAGE: string = 'random/';

  API_SELECTOR_INVENTORY: string = 'inventory/';
  API_SELECTOR_ITEMS: string = 'items/';
  API_SELECTOR_GROUPS: string = 'groups/';
  API_SELECTOR_TRANSACTIONS: string = 'transactions/';

  token: string = '';
  user = signal<TAGUser>(undefined);
  config = signal<TAGConfig>(new TAGConfig());

  onLogin: Promise<void> = null;

  isLoggedIn = computed(() => {
    return this.user() != undefined && this.user() != null && this.token != '';
  });

  constructor() {
    if (isPlatformServer(this.platformId)) {
      this.API_URL = process.env['SERVER_API_URL'] ?? this.API_URL;

      this.onLogin = new Promise<void>(async (resolve, reject) => {
        try {
          await this.getConfig();
          resolve();
        } catch (reason) {
          reject(reason);
        }
      }).then(() => {
        this.onLogin = null;
      });
    } else {
      this.onLogin = new Promise<void>(async (resolve, reject) => {
        try {
          await this.getConfig();

          if (this.cookieService.check('token')) {
            this.token = this.cookieService.get('token');
          }

          this.getUserInfo()
            .then((result) => {
              console.log('LOGIN CHECK PASS');
              resolve();
            })
            .catch((reason) => {
              console.log('LOGIN CHECK FAIL');
              this.cookieService.deleteAll();
              this.token = '';
              this.user.set(null);
              resolve();
            });
        } catch (reason) {
          reject(reason);
        }
      }).then(() => {
        this.onLogin = null;
      });
    }

    console.log('Using API URL: ' + this.API_URL);
  }

  waitForAPI(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.onLogin != null) {
        this.onLogin
          .then(() => {
            return resolve();
          })
          .catch((reason) => {
            return reject(reason);
          });
      } else {
        return resolve();
      }
    }).then(() => {
      this.onLogin = null;
    });
  }

  hasPermission(permission: string | TAGPermission) {
    if (!this.isLoggedIn()) return false;
    if (permission instanceof TAGPermission) return this.user().hasPermission(permission);
    else if (typeof permission === 'string')
      return this.user().hasPermission(TAGPermission.fromName(permission));
    else throw new Error('Invalid permission type in hasPermission in ApiService');
  }

  //#region General Stuff

  login(username: string, password: string) {
    return new Promise<TAGUser>((resolve, reject) => {
      let hash = sha256(password);

      this.http
        .post(this.API_URL + 'user/login', { username: username, password: hash })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              var user: TAGUser = new TAGUser(data.user);

              this.token = data.token;
              this.user.set(user);

              this.cookieService.set('username', username, 30, '/');
              this.cookieService.set('token', this.token, 30, '/');

              resolve(user);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => {
            /* console.log(err);
            if (typeof err == 'string') reject(new APIError(err, -1, -1));
            if (err instanceof HttpErrorResponse)
              reject(new APIError(err.error.message, err.status, err.error.error));
            else reject(new APIError(err.toString(), -1, -1)); */
            reject(err);
          },
        });
    });
  }

  pinLogin(pin: string) {
    return new Promise<TAGUser>((resolve, reject) => {
      this.http.post(this.API_URL + 'user/pinlogin', { pin: pin }).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            var user: TAGUser = new TAGUser(data.user);

            this.token = data.token;
            this.user.set(user);

            this.cookieService.set('username', user.username, 30, '/');
            this.cookieService.set('token', this.token, 30, '/');

            resolve(user);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  logout() {
    this.token = '';
    this.user.set(null);

    this.cookieService.deleteAll();
    this.cookieService.delete('username');
    this.cookieService.delete('username', '/');
    this.cookieService.delete('username', '/', 'localhost');
    this.cookieService.delete('username', '/', 'https://technikflg.com');
    this.cookieService.delete('username', '/internal');
    this.cookieService.delete('username', '/internal', 'localhost');
    this.cookieService.delete('username', '/internal', 'https://technikflg.com');

    this.cookieService.delete('token');
    this.cookieService.delete('token', '/');
    this.cookieService.delete('token', '/', 'localhost');
    this.cookieService.delete('token', '/', 'https://technikflg.com');
    this.cookieService.delete('token', '/internal');
    this.cookieService.delete('token', '/internal', 'localhost');
    this.cookieService.delete('token', '/internal', 'https://technikflg.com');
  }

  getDocumentByUrl(url: string) {
    return new Promise<Blob>((resolve, reject) => {
      this.http.get(url, { responseType: 'blob' }).subscribe({
        next: (data: Blob) => {
          resolve(data);
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  //#endregion

  //#region Config

  getConfig() {
    return new Promise<void>((resolve, reject) => {
      this.http.get(this.API_URL + this.API_SELECTOR_CONFIG).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            this.config.set(new TAGConfig(data));
            console.log('Got Config');
            resolve();
          } else {
            reject();
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  saveConfig() {
    return new Promise<void>((resolve, reject) => {
      this.http
        .put(this.API_URL + this.API_SELECTOR_CONFIG, { config: this.config().serialize() })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              this.config.set(new TAGConfig(data));
              resolve();
            } else reject();
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  //#endregion

  //#region Users

  getUserInfo() {
    return new Promise<TAGUser>((resolve, reject) => {
      this.http.get(this.API_URL + this.API_SELECTOR_USER).subscribe({
        next: (data: any) => {
          console.log(data);
          if (data.error == 0) {
            var user: TAGUser = new TAGUser(data.user);
            this.user.set(user);
            resolve(user);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  getUserMailInfo() {
    return new Promise<{ email: string; password: string }>((resolve, reject) => {
      this.http.get(this.API_URL + this.API_SELECTOR_USER + 'email').subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve(data);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  getUsers() {
    return new Promise<TAGUser[]>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.get(this.API_URL + this.API_SELECTOR_USERS).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            var users: TAGUser[] = [];
            data.users.forEach((usr) => {
              users.push(new TAGUser(usr));
            });
            resolve(users);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  getUser(userid: string) {
    return new Promise<TAGUser>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.get(this.API_URL + this.API_SELECTOR_USERS + userid).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            var user: TAGUser = new TAGUser(data.user);
            resolve(user);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  updateUser(user: TAGUser) {
    return new Promise<string>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .put(this.API_URL + this.API_SELECTOR_USERS + user._id, { user: user.serialize() })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(data.userid);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  deleteUser(userid: string) {
    return new Promise<void>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.delete(this.API_URL + this.API_SELECTOR_USERS + userid).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve();
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  newUser(user: TAGUser) {
    return new Promise<string>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.post(this.API_URL + this.API_SELECTOR_USERS, { user: user.serialize() }).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve(data.userid);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  //#endregion

  //#region Presence

  getPresence(id: string) {
    return new Promise<TAGPresence>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.get(this.API_URL + this.API_SELECTOR_PRESENCE + id).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve(new TAGPresence(data.presence));
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  getPresences() {
    return new Promise<TAGPresence[]>((resolve, reject) => {
      this.http.get(this.API_URL + this.API_SELECTOR_PRESENCE).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            let presence: TAGPresence[] = data.presence.map((pres) => {
              return new TAGPresence(pres);
            });
            resolve(presence);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  newPresence(presence: TAGPresence) {
    return new Promise<string>((resolve, reject) => {
      this.http
        .post(this.API_URL + this.API_SELECTOR_PRESENCE, { presence: presence.serialize() })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(data.presenceid);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  updatePresence(presence: TAGPresence) {
    return new Promise<string>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .put(this.API_URL + this.API_SELECTOR_PRESENCE + presence._id, {
          presence: presence.serialize(),
        })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(data.presenceid);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  deletePresence(id: string) {
    return new Promise<void>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.delete(this.API_URL + this.API_SELECTOR_PRESENCE + id).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve();
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  //#endregion

  //#region Todos

  getTodos() {
    return new Promise<TAGTodo[]>((resolve, reject) => {
      this.http.get(this.API_URL + this.API_SELECTOR_TODOS).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            let todos: TAGTodo[] = data.todos.map((todo) => {
              return new TAGTodo(todo);
            });
            resolve(todos);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  getTodo(todoid: string) {
    return new Promise<TAGTodo>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.get(this.API_URL + this.API_SELECTOR_TODOS + todoid).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve(new TAGTodo(data.todo));
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  getTodosByStatus(status: TodoStatus) {
    return new Promise<TAGTodo[]>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.get(this.API_URL + this.API_SELECTOR_TODOS + 'status/' + status.name).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            let todos: TAGTodo[] = data.todos.map((todo) => {
              return new TAGTodo(todo);
            });
            resolve(todos);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  getTodosByPriority(priority: TodoPriority) {
    return new Promise<TAGTodo[]>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .get(this.API_URL + this.API_SELECTOR_TODOS + 'priority/' + priority.name)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              let todos: TAGTodo[] = data.todos.map((todo) => {
                return new TAGTodo(todo);
              });
              resolve(todos);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getTodosByAssignee(assignee: string) {
    return new Promise<TAGTodo[]>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.get(this.API_URL + this.API_SELECTOR_TODOS + 'assignee/' + assignee).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            let todos: TAGTodo[] = data.todos.map((todo) => {
              return new TAGTodo(todo);
            });
            resolve(todos);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  newTodo(todo: TAGTodo) {
    return new Promise<string>((resolve, reject) => {
      this.http.post(this.API_URL + this.API_SELECTOR_TODOS, { todo: todo.serialize() }).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve(data.todoid);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  updateTodo(todo: TAGTodo) {
    return new Promise<string>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .put(this.API_URL + this.API_SELECTOR_TODOS + todo._id, { todo: todo.serialize() })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(data.todoid);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  deleteTodo(todoid: string) {
    return new Promise<void>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.delete(this.API_URL + this.API_SELECTOR_TODOS + todoid).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve();
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  //#endregion

  //#region Requests

  getRequest(requestid: string) {
    return new Promise<TAGRequest>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.get(this.API_URL + this.API_SELECTOR_REQUESTS + requestid).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve(new TAGRequest(data.request));
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  getRequests() {
    return new Promise<TAGRequest[]>((resolve, reject) => {
      this.http.get(this.API_URL + this.API_SELECTOR_REQUESTS).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            let requests: TAGRequest[] = data.requests.map((req) => {
              return new TAGRequest(req);
            });
            resolve(requests);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  newRequest(request: TAGRequest) {
    return new Promise<string>((resolve, reject) => {
      this.http
        .post(this.API_URL + this.API_SELECTOR_REQUESTS, { request: request.serialize() })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(data.requestid);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  updateRequest(request: TAGRequest) {
    return new Promise<string>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .put(this.API_URL + this.API_SELECTOR_REQUESTS + request._id, {
          request: request.serialize(),
        })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(data.requestid);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  deleteRequest(requestid: string) {
    return new Promise<void>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.delete(this.API_URL + this.API_SELECTOR_REQUESTS + requestid).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve();
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  promoteRequest(requestid: string) {
    return new Promise<string>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .put(this.API_URL + this.API_SELECTOR_REQUESTS + 'promote/' + requestid, {})
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(data.eventid);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  //#endregion

  //#region Events

  getEvent(eventid: string) {
    return new Promise<TAGEvent>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.get(this.API_URL + this.API_SELECTOR_EVENTS + eventid).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve(new TAGEvent(data.event));
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  getEvents(onlypublic: boolean = false) {
    return new Promise<TAGEvent[]>((resolve, reject) => {
      if (!this.isLoggedIn() && !onlypublic) reject();

      this.http
        .get(this.API_URL + this.API_SELECTOR_EVENTS + (onlypublic ? 'public' : ''))
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              let events: TAGEvent[] = data.events.map((evt) => {
                return new TAGEvent(evt);
              });
              resolve(events);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  updateEvent(event: TAGEvent) {
    return new Promise<TAGEvent>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .put(this.API_URL + this.API_SELECTOR_EVENTS + event._id, { event: event.serialize() })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGEvent(data.event));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  deleteEvent(eventid: string) {
    return new Promise<void>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.delete(this.API_URL + this.API_SELECTOR_EVENTS + eventid).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve();
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  newEvent(event: TAGEvent) {
    return new Promise<TAGEvent>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .post(this.API_URL + this.API_SELECTOR_EVENTS, { event: event.serialize() })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGEvent(data.event));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getAllEventAttachments(eventID: string) {
    return new Promise<{ fileName: string; mimeType: string }[]>((resolve, reject) => {
      this.http.get(this.API_URL + this.API_SELECTOR_EVENTS + eventID + '/file').subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve(data.files);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  getEventAttachment(eventID: string, fileName: string) {
    return new Promise<any>((resolve, reject) => {
      this.http
        .get(this.API_URL + this.API_SELECTOR_EVENTS + eventID + '/file/' + fileName, {
          responseType: 'blob',
        })
        .subscribe({
          next: (data: any) => {
            resolve(data);
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  uploadEventAttachment(
    eventID: string,
    fileName: string,
    fileData: string,
    progress: (progress) => void,
  ) {
    return new Promise<TAGEvent>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .post(
          this.API_URL + this.API_SELECTOR_EVENTS + eventID + '/file/',
          { fileName: fileName, file: fileData },
          {
            reportProgress: true,
            observe: 'events',
          },
        )
        .subscribe({
          next: (event) => {
            console.log('progress: ');
            console.log(event);

            if (event.type == HttpEventType.UploadProgress) {
              const percentDone = Math.round((100 * event.loaded) / event.total);
              console.log(`File is ${percentDone}% loaded.`);
              progress(event.loaded / event.total);
            } else if (event instanceof HttpResponse) {
              console.log('File is completely loaded!');
              console.log(event.body as any);

              if ((event.body as any).error == 0) {
                resolve(new TAGEvent((event as HttpResponse<any>).body.event));
              } else {
                reject(event.body);
              }
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  deleteEventAttachment(eventID: string, fileName: string) {
    return new Promise<TAGEvent>((resolve, reject) => {
      this.http
        .delete(this.API_URL + this.API_SELECTOR_EVENTS + eventID + '/file/' + fileName)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGEvent(data.event));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  //#endregion

  //#region Song Requests

  getSongRequests() {
    return new Promise<TAGSongRequest[]>((resolve, reject) => {
      this.http.get(this.API_URL + this.API_SELECTOR_SONGREQUESTS).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            let songRequests: TAGSongRequest[] = data.songrequests.map((sr) => {
              return new TAGSongRequest(sr);
            });

            resolve(songRequests);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  newSongRequest(songRequest: TAGSongRequest) {
    return new Promise<string>((resolve, reject) => {
      this.http
        .post(this.API_URL + this.API_SELECTOR_SONGREQUESTS, {
          songRequest: songRequest.serialize(),
        })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(data.songRequestId);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  updateSongRequest(songRequest: Partial<TAGSongRequest>) {
    return new Promise<TAGSongRequest>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .put(this.API_URL + this.API_SELECTOR_SONGREQUESTS + songRequest._id, {
          songRequest: songRequest.serialize(),
        })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGSongRequest(data.songRequest));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  voteForSongRequest(songRequestId: string) {
    return new Promise<void>((resolve, reject) => {
      this.http
        .post(
          this.API_URL + this.API_SELECTOR_SONGREQUESTS + this.API_SELECTOR_VOTE + songRequestId,
          {},
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve();
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  deleteSongRequest(songRequestId: string) {
    return new Promise<void>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.delete(this.API_URL + this.API_SELECTOR_SONGREQUESTS + songRequestId).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve();
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  deleteAllSongRequests() {
    return new Promise<void>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.delete(this.API_URL + this.API_SELECTOR_SONGREQUESTS).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve();
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  //#endregion

  //#region Image Categories

  getImageCategories() {
    return new Promise<TAGImageCategory[]>((resolve, reject) => {
      this.http.get(this.API_URL + this.API_SELECTOR_CATEGORIES).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            let categories: TAGImageCategory[] = data.categories.map((cat) => {
              return new TAGImageCategory(cat);
            });

            resolve(categories);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  updateImageCategory(category: TAGImageCategory) {
    return new Promise<string>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .put(this.API_URL + this.API_SELECTOR_CATEGORIES + category._id, {
          category: category.serialize(),
        })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(data.categoryid);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  deleteImageCategory(categoryid: string) {
    return new Promise<void>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.delete(this.API_URL + this.API_SELECTOR_CATEGORIES + categoryid).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve();
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  newImageCategory(category: TAGImageCategory) {
    return new Promise<string>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .post(this.API_URL + this.API_SELECTOR_CATEGORIES, { category: category.serialize() })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(data.categoryid);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getImageCategoryForEvent(eventid: string) {
    return new Promise<TAGImageCategory>((resolve, reject) => {
      this.http
        .get(this.API_URL + this.API_SELECTOR_CATEGORIES + this.API_SELECTOR_EVENTS + eventid)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGImageCategory(data.category));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  //#endregion

  //#region Images

  getImageFull(imageid: string) {
    return new Promise<string>((resolve, reject) => {
      this.http
        .get(this.API_URL + this.API_SELECTOR_IMAGE + imageid + '/' + this.API_SELECTOR_FULLIMAGE)
        .subscribe({
          next: (data: any) => {
            resolve('data:image/jpeg;base64,' + data);
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getImageThumb(imageid: string) {
    return new Promise<string>((resolve, reject) => {
      this.http
        .get(this.API_URL + this.API_SELECTOR_IMAGE + imageid + '/' + this.API_SELECTOR_THUMBNAIL)
        .subscribe({
          next: (data: any) => {
            //TODO WTH?
            console.log(data);
            resolve('data:image/jpeg;base64,' + data);

            /* if (data.error == 0) {
          resolve(data.imageData);
        } else {
          reject(data);
        } */
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getImages(categoryid: string) {
    return new Promise<TAGImage[]>((resolve, reject) => {
      this.http.get(this.API_URL + this.API_SELECTOR_IMAGES + categoryid).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            let images: TAGImage[] = data.images.map((img) => {
              return new TAGImage(img);
            });

            resolve(images);
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  deleteImage(imageid: string) {
    return new Promise<void>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http.delete(this.API_URL + this.API_SELECTOR_IMAGE + imageid).subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve();
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  newImage(image: TAGImage, imageFile: string, progress: (progress: number) => void) {
    return new Promise<string>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      this.http
        .post(
          this.API_URL + this.API_SELECTOR_IMAGE,
          { image: image.serialize(), imageFile: imageFile },
          {
            reportProgress: true,
            observe: 'events',
          },
        )
        .subscribe({
          next: (event) => {
            console.log('progress: ');
            console.log(event);
            if (event.type == HttpEventType.UploadProgress) {
              const percentDone = Math.round((100 * event.loaded) / event.total);
              console.log(`File is ${percentDone}% loaded.`);
              progress(event.loaded / event.total);
            } else if (event instanceof HttpResponse) {
              console.log('File is completely loaded!');
              console.log(event.body as any);
              if ((event.body as any).error == 0) {
                resolve((event as HttpResponse<any>).body.imageid);
              } else {
                reject(event.body);
              }
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  updateImage(image: TAGImage, imageFile: string = null, progress: (progress) => void = undefined) {
    return new Promise<string>((resolve, reject) => {
      if (!this.isLoggedIn()) reject();

      var body: any = { image: image.serialize() };

      if (imageFile != null) {
        body['imageFile'] = imageFile;
      }

      this.http
        .put(this.API_URL + this.API_SELECTOR_IMAGE + image._id, body, {
          reportProgress: true,
          observe: 'events',
        })
        .subscribe({
          next: (event) => {
            console.log('progress: ');
            console.log(event);
            if (event.type == HttpEventType.UploadProgress) {
              const percentDone = Math.round((100 * event.loaded) / event.total);
              console.log(`File is ${percentDone}% loaded.`);
              if (progress) progress(event.loaded / event.total);
            } else if (event instanceof HttpResponse) {
              console.log('File is completely loaded!');
              console.log(event.body as any);
              if ((event.body as any).error == 0) {
                resolve((event as HttpResponse<any>).body.imageid);
              } else {
                reject(event.body);
              }
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getRandomImage(category: string) {
    return new Promise<TAGImage>((resolve, reject) => {
      this.http
        .get(
          this.API_URL +
            this.API_SELECTOR_IMAGE +
            this.API_SELECTOR_RANDOMIMAGE +
            (category != '' ? category : ''),
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGImage(data.image));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getImageLink(imageid: string) {
    return this.API_URL + this.API_SELECTOR_IMAGE + imageid + '/' + this.API_SELECTOR_FULLIMAGE;
  }

  getThumbLink(imageid: string) {
    return this.API_URL + this.API_SELECTOR_IMAGE + imageid + '/' + this.API_SELECTOR_THUMBNAIL;
  }

  //#endregion

  //#region Finance Transactions

  getFinanceTransactions() {
    return new Promise<TAGFinanceTransaction[]>((resolve, reject) => {
      this.http
        .get(this.API_URL + this.API_SELECTOR_FINANCES + this.API_SELECTOR_TRANSACTIONS)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              let transactions: TAGFinanceTransaction[] = data.transactions.map((trans) => {
                return new TAGFinanceTransaction(trans);
              });

              resolve(transactions);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getFinanceTransactionsByUser(user: string) {
    return new Promise<TAGFinanceTransaction[]>((resolve, reject) => {
      this.http
        .get(
          this.API_URL +
            this.API_SELECTOR_FINANCES +
            this.API_SELECTOR_TRANSACTIONS +
            this.API_SELECTOR_USER +
            user,
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              let transactions: TAGFinanceTransaction[] = data.transactions.map((trans) => {
                return new TAGFinanceTransaction(trans);
              });

              resolve(transactions);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getFinanceTransactionsByEvent(eventid: string) {
    return new Promise<TAGFinanceTransaction[]>((resolve, reject) => {
      this.http
        .get(
          this.API_URL +
            this.API_SELECTOR_FINANCES +
            this.API_SELECTOR_TRANSACTIONS +
            'event/' +
            eventid,
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              let transactions: TAGFinanceTransaction[] = data.transactions.map((trans) => {
                return new TAGFinanceTransaction(trans);
              });

              resolve(transactions);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getFinanceTransaction(id: string) {
    return new Promise<TAGFinanceTransaction>((resolve, reject) => {
      this.http
        .get(this.API_URL + this.API_SELECTOR_FINANCES + this.API_SELECTOR_TRANSACTIONS + id)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGFinanceTransaction(data.transaction));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getNextInvoiceNumber() {
    return new Promise<number>((resolve, reject) => {
      this.http.get(this.API_URL + this.API_SELECTOR_FINANCES + 'invoice/next').subscribe({
        next: (data: any) => {
          if (data.error == 0) {
            resolve(parseInt(data.next));
          } else {
            reject(data);
          }
        },
        error: (err: APIError) => reject(err),
      });
    });
  }

  newFinanceTransaction(transaction: TAGFinanceTransaction) {
    return new Promise<TAGFinanceTransaction>((resolve, reject) => {
      this.http
        .post(this.API_URL + this.API_SELECTOR_FINANCES + this.API_SELECTOR_TRANSACTIONS, {
          transaction: transaction.serialize(),
        })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGFinanceTransaction(data.transaction));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  updateFinanceTransaction(transaction: TAGFinanceTransaction) {
    return new Promise<TAGFinanceTransaction>((resolve, reject) => {
      this.http
        .put(
          this.API_URL +
            this.API_SELECTOR_FINANCES +
            this.API_SELECTOR_TRANSACTIONS +
            transaction._id,
          { transaction: transaction.serialize() },
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGFinanceTransaction(data.transaction));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  deleteFinanceTransaction(transactionID: string) {
    return new Promise<void>((resolve, reject) => {
      this.http
        .delete(
          this.API_URL +
            this.API_SELECTOR_FINANCES +
            this.API_SELECTOR_TRANSACTIONS +
            transactionID,
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve();
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getAllFinanceTransactionAttachments(transactionID: string) {
    return new Promise<{ fileName: string; mimeType: string }[]>((resolve, reject) => {
      this.http
        .get(
          this.API_URL +
            this.API_SELECTOR_FINANCES +
            this.API_SELECTOR_TRANSACTIONS +
            transactionID +
            '/file',
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(data.files);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getPublicFinanceTransactionInvoice(transactionID: string) {
    return new Promise<Blob>((resolve, reject) => {
      this.http
        .get(
          this.API_URL +
            this.API_SELECTOR_FINANCES +
            this.API_SELECTOR_TRANSACTIONS +
            transactionID +
            '/file/overview',
          { responseType: 'blob' },
        )
        .subscribe({
          next: (data: Blob) => {
            resolve(data);
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getFinanceTransactionAttachment(transactionID: string, fileName: string) {
    return new Promise<Blob>((resolve, reject) => {
      this.http
        .get(
          this.API_URL +
            this.API_SELECTOR_FINANCES +
            this.API_SELECTOR_TRANSACTIONS +
            transactionID +
            '/file/' +
            fileName,
          { responseType: 'blob' },
        )
        .subscribe({
          next: (data: Blob) => {
            resolve(data);
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  uploadFinanceTransactionAttachment(
    transactionID: string,
    fileName: string,
    fileData: string,
    progress: (progress) => void,
  ) {
    return new Promise<TAGFinanceTransaction>((resolve, reject) => {
      const options = {
        reportProgress: true,
        observe: 'events',
      };

      this.http
        .post(
          this.API_URL +
            this.API_SELECTOR_FINANCES +
            this.API_SELECTOR_TRANSACTIONS +
            transactionID +
            '/file/',
          { fileName: fileName, file: fileData },
          {
            reportProgress: true,
            observe: 'events',
          },
        )
        .subscribe({
          next: (event) => {
            console.log('progress: ');
            console.log(event);

            if (event.type == HttpEventType.UploadProgress) {
              const percentDone = Math.round((100 * event.loaded) / event.total);
              console.log(`File is ${percentDone}% loaded.`);
              progress(event.loaded / event.total);
            } else if (event instanceof HttpResponse) {
              console.log('File is completely loaded!');
              console.log(event.body as any);

              if ((event.body as any).error == 0) {
                resolve(new TAGFinanceTransaction((event as HttpResponse<any>).body.transaction));
              } else {
                reject(event.body);
              }
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  deleteFinanceTransactionAttachment(transactionID: string, fileName: string) {
    return new Promise<TAGFinanceTransaction>((resolve, reject) => {
      this.http
        .delete(
          this.API_URL +
            this.API_SELECTOR_FINANCES +
            this.API_SELECTOR_TRANSACTIONS +
            transactionID +
            '/file/' +
            fileName,
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGFinanceTransaction(data.transaction));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  //#endregion

  //#region Inventory Items

  getInvItems() {
    return new Promise<TAGInvItemType[]>((resolve, reject) => {
      this.http
        .get(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_ITEMS)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              let items: TAGInvItemType[] = data.items.map((itm) => {
                return new TAGInvItemType(itm);
              });

              resolve(items);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  findInvItem(idorcode: string) {
    return new Promise<TAGInvItemType>((resolve, reject) => {
      this.http
        .get(
          this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_ITEMS + 'find/' + idorcode,
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGInvItemType(data.item));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getInvItem(id: string) {
    return new Promise<TAGInvItemType>((resolve, reject) => {
      this.http
        .get(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_ITEMS + id)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGInvItemType(data.item));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  newInvItem(item: TAGInvItemType) {
    return new Promise<TAGInvItemType>((resolve, reject) => {
      this.http
        .post(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_ITEMS, {
          item: item.serialize(),
        })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGInvItemType(data.item));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  updateInvItem(item: TAGInvItemType) {
    return new Promise<TAGInvItemType>((resolve, reject) => {
      this.http
        .put(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_ITEMS + item._id, {
          item: item.serialize(),
        })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGInvItemType(data.item));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  deleteInvItem(id: string) {
    return new Promise<void>((resolve, reject) => {
      this.http
        .delete(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_ITEMS + id)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve();
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  //#endregion

  //#region Inventory Transactions

  getInvTransactions() {
    return new Promise<TAGInvTransaction[]>((resolve, reject) => {
      this.http
        .get(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_TRANSACTIONS)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              let transactions: TAGInvTransaction[] = data.transactions.map((trans) => {
                return new TAGInvTransaction(trans);
              });

              resolve(transactions);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getInvTransactionsByItem(itemid: string) {
    return new Promise<TAGInvTransaction[]>((resolve, reject) => {
      this.http
        .get(
          this.API_URL +
            this.API_SELECTOR_INVENTORY +
            this.API_SELECTOR_TRANSACTIONS +
            this.API_SELECTOR_ITEMS +
            itemid,
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              let transactions: TAGInvTransaction[] = data.transactions.map((trans) => {
                return new TAGInvTransaction(trans);
              });

              resolve(transactions);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getInvTransactionsByEvent(eventid: string) {
    return new Promise<TAGInvTransaction[]>((resolve, reject) => {
      this.http
        .get(
          this.API_URL +
            this.API_SELECTOR_INVENTORY +
            this.API_SELECTOR_TRANSACTIONS +
            'event/' +
            eventid,
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              let transactions: TAGInvTransaction[] = data.transactions.map((trans) => {
                return new TAGInvTransaction(trans);
              });

              resolve(transactions);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getInvTransaction(id: string) {
    return new Promise<TAGInvTransaction>((resolve, reject) => {
      this.http
        .get(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_TRANSACTIONS + id)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGInvTransaction(data.transaction));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  createTransaction(transaction: TAGInvTransaction) {
    return new Promise<TAGInvTransaction>((resolve, reject) => {
      this.http
        .post(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_TRANSACTIONS, {
          transaction: transaction.serialize(),
        })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGInvTransaction(data.transaction));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  updateTransaction(transaction: TAGInvTransaction) {
    return new Promise<TAGInvTransaction>((resolve, reject) => {
      this.http
        .put(
          this.API_URL +
            this.API_SELECTOR_INVENTORY +
            this.API_SELECTOR_TRANSACTIONS +
            transaction._id,
          { transaction: transaction.serialize() },
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGInvTransaction(data.transaction));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  addTransactionItems(transactionID: string, items: { itemid: string; instanceid: string }[]) {
    return new Promise<TAGInvTransaction>((resolve, reject) => {
      this.http
        .put(
          this.API_URL +
            this.API_SELECTOR_INVENTORY +
            this.API_SELECTOR_TRANSACTIONS +
            transactionID +
            '/add',
          { items: items },
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGInvTransaction(data.transaction));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  returnTransactionItems(transactionID: string, items: { itemid: string; instanceid: string }[]) {
    return new Promise<TAGInvTransaction>((resolve, reject) => {
      this.http
        .put(
          this.API_URL +
            this.API_SELECTOR_INVENTORY +
            this.API_SELECTOR_TRANSACTIONS +
            transactionID +
            '/back',
          { items: items },
        )
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGInvTransaction(data.transaction));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  //#endregion

  //#region Inventory Groups

  getInvItemGroups() {
    return new Promise<TAGInvItemGroup[]>((resolve, reject) => {
      this.http
        .get(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_GROUPS)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              let groups: TAGInvItemGroup[] = data.groups.map((grp) => {
                return new TAGInvItemGroup(grp);
              });
              resolve(groups);
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  getInvItemGroup(id: string) {
    return new Promise<TAGInvItemGroup>((resolve, reject) => {
      this.http
        .get(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_GROUPS + id)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGInvItemGroup(data.group));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  createInvItemGroup(group: TAGInvItemGroup) {
    return new Promise<TAGInvItemGroup>((resolve, reject) => {
      this.http
        .post(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_GROUPS, {
          group: group.serialize(),
        })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGInvItemGroup(data.group));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  updateInvItemGroup(group: TAGInvItemGroup) {
    return new Promise<TAGInvItemGroup>((resolve, reject) => {
      this.http
        .put(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_GROUPS + group._id, {
          group: group.serialize(),
        })
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve(new TAGInvItemGroup(data.group));
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  deleteInvItemGroup(id: string) {
    return new Promise<void>((resolve, reject) => {
      this.http
        .delete(this.API_URL + this.API_SELECTOR_INVENTORY + this.API_SELECTOR_GROUPS + id)
        .subscribe({
          next: (data: any) => {
            if (data.error == 0) {
              resolve();
            } else {
              reject(data);
            }
          },
          error: (err: APIError) => reject(err),
        });
    });
  }

  //#endregion

  //#region OAuth

  // The OAuth interaction is tracked with a cookie of the API, so these requests need withCredentials

  getOAuthInteraction(uid: string) {
    return new Promise<OAuthInteraction>((resolve, reject) => {
      this.http.get(this.API_URL + 'oidc-ui/' + uid, { withCredentials: true }).subscribe({
        next: (data: any) => resolve(data),
        error: (err: APIError) => reject(err),
      });
    });
  }

  acceptOAuthInteraction(uid: string) {
    if (!this.isLoggedIn()) return Promise.reject(new APIError('Not logged in', 401, 1));

    return new Promise<string>((resolve, reject) => {
      this.http
        .post(this.API_URL + 'oidc-ui/' + uid + '/confirm', {}, { withCredentials: true })
        .subscribe({
          next: (data: any) => resolve(data.redirect),
          error: (err: APIError) => reject(err),
        });
    });
  }

  declineOAuthInteraction(uid: string) {
    return new Promise<string>((resolve, reject) => {
      this.http
        .post(this.API_URL + 'oidc-ui/' + uid + '/decline', {}, { withCredentials: true })
        .subscribe({
          next: (data: any) => resolve(data.redirect),
          error: (err: APIError) => reject(err),
        });
    });
  }

  //#endregion
}
